// apps/frontend/src/pages/UsersPage.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  type SelectChangeEvent,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  type AdminUserResponse,
  type CreateUserRequest,
  type Permission,
  type UpdateUserRequest,
  type UserRole,
  PERMISSIONS,
  USER_ROLES,
} from "@bale-bot/types";
import { api } from "../services/api";
import { useAuthStore } from "../store/auth.store";
import { describeError } from "../services/api";
import PageHeader from "../components/PageHeader";

const ALL_PERMISSIONS = Object.values(PERMISSIONS) as Permission[];

export default function UsersPage() {
  const currentUser = useAuthStore((s) => s.user);

  const [users, setUsers] = useState<AdminUserResponse[]>([]);
  const [loading, setLoading] = useState(true); // only for the very first load
  const [refreshing, setRefreshing] = useState(false); // for later reloads
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Create form
  const [createOpen, setCreateOpen] = useState(false);
  const [createUsername, setCreateUsername] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createRole, setCreateRole] = useState<UserRole>("viewer");
  const [createPermissions, setCreatePermissions] = useState<Permission[]>([]);
  const [createUseExplicit, setCreateUseExplicit] = useState(false);

  // Edit dialog
  const [editUser, setEditUser] = useState<AdminUserResponse | null>(null);
  const [editRole, setEditRole] = useState<UserRole>("viewer");
  const [editDisabled, setEditDisabled] = useState(false);
  const [editPassword, setEditPassword] = useState("");
  const [editPermissions, setEditPermissions] = useState<Permission[]>([]);
  const [editUseRoleDefaults, setEditUseRoleDefaults] = useState(false);

  // Delete confirm
  const [deleteUser, setDeleteUser] = useState<AdminUserResponse | null>(null);

  const loadUsers = useCallback(async (isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);

    try {
      const items = await api.getUsers();
      setUsers(items);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load only
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const items = await api.getUsers();
        if (!cancelled) {
          setUsers(items);
        }
      } catch (err) {
        if (!cancelled) {
          setError(describeError(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const isSelf = useCallback(
    (u: AdminUserResponse) => currentUser?.username === u.username,
    [currentUser],
  );

  const handleCreate = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const payload: CreateUserRequest = {
        username: createUsername.trim(),
        password: createPassword,
        role: createRole,
      };
      if (createUseExplicit && createPermissions.length > 0) {
        payload.permissions = createPermissions;
      }
      await api.createUser(payload);
      setSuccess("User created");
      setCreateOpen(false);
      setCreateUsername("");
      setCreatePassword("");
      setCreateRole("viewer");
      setCreatePermissions([]);
      setCreateUseExplicit(false);
      await loadUsers();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }, [
    busy,
    createUsername,
    createPassword,
    createRole,
    createPermissions,
    createUseExplicit,
    loadUsers,
  ]);

  const openEdit = useCallback((u: AdminUserResponse) => {
    setEditUser(u);
    setEditRole(u.role);
    setEditDisabled(u.disabled);
    setEditPassword("");
    setEditPermissions([...u.permissions]);
    setEditUseRoleDefaults(false);
  }, []);

  const editChanged = useMemo(() => {
    if (!editUser) return false;
    if (editRole !== editUser.role) return true;
    if (editDisabled !== editUser.disabled) return true;
    if (editPassword.length > 0) return true;
    if (editUseRoleDefaults) return true;
    const current = [...editUser.permissions].sort().join(",");
    const next = [...editPermissions].sort().join(",");
    return current !== next;
  }, [
    editUser,
    editRole,
    editDisabled,
    editPassword,
    editPermissions,
    editUseRoleDefaults,
  ]);

  const handleEdit = useCallback(async () => {
    if (!editUser || busy || !editChanged) return;
    setBusy(true);
    setError(null);
    try {
      const payload: UpdateUserRequest = {};
      if (editRole !== editUser.role) payload.role = editRole;
      if (editDisabled !== editUser.disabled) payload.disabled = editDisabled;
      if (editPassword.length > 0) payload.password = editPassword;
      if (editUseRoleDefaults) {
        payload.permissions = null;
      } else {
        const current = [...editUser.permissions].sort().join(",");
        const next = [...editPermissions].sort().join(",");
        if (current !== next) payload.permissions = editPermissions;
      }
      await api.updateUser(editUser.id, payload);
      setSuccess("User updated");
      setEditUser(null);
      await loadUsers();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }, [
    editUser,
    busy,
    editChanged,
    editRole,
    editDisabled,
    editPassword,
    editPermissions,
    editUseRoleDefaults,
    loadUsers,
  ]);

  const handleDelete = useCallback(async () => {
    if (!deleteUser || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteUser(deleteUser.id);
      setSuccess("User deleted");
      setDeleteUser(null);
      await loadUsers();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }, [deleteUser, busy, loadUsers]);

  const canSubmitCreate =
    createUsername.trim().length >= 3 && createPassword.length >= 8 && !busy;

  return (
    <Stack spacing={3} className="stagger">
      <PageHeader
        title="Users"
        description="Admin panel accounts and permissions."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            disabled={busy}
          >
            Create user
          </Button>
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer>
            {refreshing && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <CircularProgress size={18} />
              </Box>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => {
                  const self = isSelf(u);
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "center" }}
                        >
                          <span>{u.username}</span>
                          {self && (
                            <Chip
                              label="you"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {u.isDefault && (
                            <Chip
                              label="default"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{u.role}</TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ flexWrap: "wrap" }}
                          useFlexGap
                        >
                          {u.permissions.map((p) => (
                            <Chip key={p} label={p} size="small" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {u.disabled ? (
                          <Chip label="Disabled" size="small" color="default" />
                        ) : (
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(u.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ justifyContent: "flex-end" }}
                        >
                          <Tooltip title="Edit">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openEdit(u)}
                                disabled={busy}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              self
                                ? "You cannot delete your own account"
                                : "Delete"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteUser(u)}
                                disabled={busy || self}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !busy && setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create user</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              value={createUsername}
              onChange={(e) => setCreateUsername(e.target.value)}
              fullWidth
              required
              slotProps={{
                input: { inputProps: { minLength: 3 } },
              }}
              disabled={busy}
            />
            <TextField
              label="Password"
              type="password"
              value={createPassword}
              onChange={(e) => setCreatePassword(e.target.value)}
              fullWidth
              required
              slotProps={{
                input: { inputProps: { minLength: 8 } },
              }}
              disabled={busy}
            />
            <FormControl fullWidth>
              <InputLabel id="create-role-label">Role</InputLabel>
              <Select
                labelId="create-role-label"
                label="Role"
                value={createRole}
                onChange={(e) => setCreateRole(e.target.value as UserRole)}
                disabled={busy}
              >
                {USER_ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={createUseExplicit}
                  onChange={(e) => setCreateUseExplicit(e.target.checked)}
                  disabled={busy}
                />
              }
              label="Set explicit permissions (otherwise role defaults apply)"
            />
            {createUseExplicit && (
              <FormControl fullWidth>
                <InputLabel id="create-perms-label">Permissions</InputLabel>
                <Select
                  labelId="create-perms-label"
                  label="Permissions"
                  multiple
                  value={createPermissions as string[]}
                  onChange={(e: SelectChangeEvent<string[]>) => {
                    const v = e.target.value;
                    setCreatePermissions(
                      typeof v === "string"
                        ? (v.split(",") as Permission[])
                        : (v as Permission[]),
                    );
                  }}
                  input={<OutlinedInput label="Permissions" />}
                  renderValue={(selected: string[]) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((v: string) => (
                        <Chip key={v} label={v} size="small" />
                      ))}
                    </Box>
                  )}
                  disabled={busy}
                >
                  {ALL_PERMISSIONS.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!canSubmitCreate}
          >
            {busy ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={!!editUser}
        onClose={() => !busy && setEditUser(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit user{editUser ? `: ${editUser.username}` : ""}
        </DialogTitle>
        <DialogContent>
          {editUser && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="edit-role-label">Role</InputLabel>
                <Select
                  labelId="edit-role-label"
                  label="Role"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as UserRole)}
                  disabled={busy || isSelf(editUser)}
                >
                  {USER_ROLES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isSelf(editUser) && (
                <Typography variant="caption" color="text.secondary">
                  You cannot demote your own account.
                </Typography>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={editDisabled}
                    onChange={(e) => setEditDisabled(e.target.checked)}
                    disabled={busy || isSelf(editUser)}
                  />
                }
                label="Disabled"
              />
              {isSelf(editUser) && (
                <Typography variant="caption" color="text.secondary">
                  You cannot disable your own account.
                </Typography>
              )}

              <TextField
                label="New password (leave blank to keep)"
                type="password"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                fullWidth
                slotProps={{
                  input: { inputProps: { minLength: 8 } },
                }}
                disabled={busy}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={editUseRoleDefaults}
                    onChange={(e) => setEditUseRoleDefaults(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Use role defaults (clear explicit permissions)"
              />

              {!editUseRoleDefaults && (
                <FormControl fullWidth>
                  <InputLabel id="edit-perms-label">
                    Permissions (override)
                  </InputLabel>
                  <Select
                    labelId="edit-perms-label"
                    label="Permissions (override)"
                    multiple
                    value={editPermissions as string[]}
                    onChange={(e: SelectChangeEvent<string[]>) => {
                      const v = e.target.value;
                      setEditPermissions(
                        typeof v === "string"
                          ? (v.split(",") as Permission[])
                          : (v as Permission[]),
                      );
                    }}
                    input={<OutlinedInput label="Permissions (override)" />}
                    renderValue={(selected: string[]) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((v: string) => (
                          <Chip key={v} label={v} size="small" />
                        ))}
                      </Box>
                    )}
                    disabled={busy}
                  >
                    {ALL_PERMISSIONS.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Typography variant="caption" color="text.secondary">
                Permissions shown are the effective set. Saving without “use
                role defaults” will store them as explicit grants.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEdit}
            disabled={busy || !editChanged}
          >
            {busy ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteUser}
        onClose={() => !busy && setDeleteUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete user</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deleteUser?.username}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={busy}
          >
            {busy ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </Stack>
  );
}